import React, { useEffect, useRef } from 'react'
import { Common } from '@trimblemaps/trimblemaps-js';
import { ZOOM_VALUE_7X, LOAD_MARKER, SHIPMENT_MAP_PADDING } from '../../../utils/constants'
import { GeographicalService } from '../../../utils/common'
import './map.scss'
import "../../../components/shared/PCMilerMap/map.scss";
import { ShipmentWithCount } from '../../../web/components/pcmiler-map/ShipmentMap'
import { getLongitudeLatitude } from './utils'
import { ExpandCollapseControl } from '../../../services/PCMiler/PCMilerClass'
import { PCMilerShipmentMapProps, UniqueLocationStopShipments, LoadStopDetails, StopDetails } from './PCMilerShipmentMap.type';

export const PCMilerShipmentMap = (props: PCMilerShipmentMapProps) => {

    const { shipments,
        fitBoundsResize,
        onLoaded,
        isDialog = null,
        onExpand = () => null,
        onCollapse = () => null,
        onShipmentsMarkerClick,
    } = props;

    const MAP_CONTAINER_DOM = useRef<HTMLDivElement>(null);
    const map = useRef<TrimbleMaps.Map>();
    const bounds = useRef<TrimbleMaps.LngLatBounds>();
    const fitBoundsOptions = useRef<TrimbleMaps.FitBoundsOptions>({});
    const markers = useRef<TrimbleMaps.Marker[]>([]);
    const cachedLatlngs = useRef<any>({});

    useEffect(() => {
        markers.current.length && markers.current.forEach(marker => marker.remove());
        renderShipmentMap();
    }, [JSON.stringify(shipments)]);

    useEffect(() => {
        if (map.current && bounds.current) mapFitBoundsResize();
    }, [fitBoundsResize]);


    async function renderShipmentMap() {
        const uniqueLocationStopShipments: UniqueLocationStopShipments[] = shipments && shipments.length ? await getUniqueLocationStopShipments(shipments) : [];
        const [{ stop: { latLng: { lng, lat } = { lng: 0, lat: 0 } } = {} }] = uniqueLocationStopShipments.length && uniqueLocationStopShipments || [{}];
        const myMapBounds: TrimbleMaps.LngLatBounds = GeographicalService.details.createBounds({ lat, lng }, { lat, lng });
        const maxZoom: number = uniqueLocationStopShipments.length ? ZOOM_VALUE_7X : 0;
        const myFitBoundsOptions: TrimbleMaps.FitBoundsOptions = { padding: SHIPMENT_MAP_PADDING, maxZoom };
        uniqueLocationStopShipments.forEach(({ stop: { latLng } }) => myMapBounds.extend(latLng));
        const style = Common.Style.DEFAULT;
        const myMap: TrimbleMaps.Map = map.current || GeographicalService.details.renderMap(false,MAP_CONTAINER_DOM.current!, myMapBounds, style, myFitBoundsOptions);
        bounds.current = myMapBounds;
        fitBoundsOptions.current = myFitBoundsOptions;
        if (!map.current) {
            const expandCollapseControl: ExpandCollapseControl | false = isDialog !== null && GeographicalService.details.renderExpandCollapseControl(isDialog, onExpand, onCollapse);
            expandCollapseControl && myMap.addControl(expandCollapseControl, "bottom-left");
            const myMapNavControl: TrimbleMaps.NavigationControl = GeographicalService.details.navControl({ showCompass: false });
            myMap.addControl(myMapNavControl, "bottom-right");
            map.current = myMap;
        } else {
            mapFitBoundsResize()
        }
        if (uniqueLocationStopShipments.length) {
            uniqueLocationStopShipments.forEach((shipmentsStop: any) => renderShipmentMapMarkers(shipmentsStop, myMap));
        }
        myMap.on('load', async function () {
            if(myMap.getLayer("labels_facility")) myMap.removeLayer("labels_facility");
            onLoaded && onLoaded();
        });

    }

    function renderShipmentMapMarkers(shipmentsStop: UniqueLocationStopShipments, mapComponent: TrimbleMaps.Map) {
        const { stop, uniqueLocationShipments } = shipmentsStop;
        const { city, state, country, latLng: { lng, lat } = { lng: 0, lat: 0 } } = stop;
        const svgContent: HTMLElement = document.createElement('div');
        svgContent.title = `${city}, ${state}, ${country}`;
        svgContent.innerHTML = LOAD_MARKER;
        svgContent.className = 'shipment-map-marker-div';
        svgContent.onclick = function () {
            onShipmentsMarkerClick(uniqueLocationShipments);
        }
        const label: HTMLElement = document.createElement('div');
        label.innerHTML = `${uniqueLocationShipments.length}`;
        label.className = 'shipment-map-marker-label';
        svgContent.appendChild(label);
        const marker: TrimbleMaps.Marker = GeographicalService.details.renderMarker(svgContent, lng, lat).addTo(mapComponent);
        markers.current.push(marker);
    }

    async function getUniqueLocationStopShipments(locationShipments: ShipmentWithCount[]): Promise<UniqueLocationStopShipments[]> {
        const uniquelocationNames: string[] = [];
        locationShipments.forEach(({ locationName }: { locationName: string }) => {
            if (!uniquelocationNames.includes(locationName)) {
                uniquelocationNames.push(locationName);
            }
        });
        const shipmentsByUniqueLocationNames = uniquelocationNames.map((uniquelocationName: string) =>
            locationShipments.filter(({ locationName }: { locationName: string }) => locationName === uniquelocationName));
        const findLoadStopTypeP = ({ loadStops }: { loadStops: any[] }) => loadStops.find(({ stopType }: any) => stopType === "P");
        return await Promise.all(shipmentsByUniqueLocationNames.map(async (uniqueLocationShipments: ShipmentWithCount[]) => ({ stop: await getStopDetails(findLoadStopTypeP(uniqueLocationShipments[0])), uniqueLocationShipments })));
    }

    async function getStopDetails(stop: LoadStopDetails): Promise<StopDetails> {
        let { city = "", state = "", country = "", stopId } = stop;
        let latLng;
        if (cachedLatlngs.current[stopId]) {
            latLng = cachedLatlngs.current[stopId];
        } else {
            latLng = await getLongitudeLatitude(stop)
            cachedLatlngs.current[stopId] = latLng;
        }
        return {
            city,
            state,
            country,
            latLng,
        }
    }

    function mapFitBoundsResize(): void {
        map.current!.resize();
        map.current!.fitBounds(bounds.current!, fitBoundsOptions.current);
    }

    return <div ref={MAP_CONTAINER_DOM} className="pc-miler-map"></div>

}